/** The subset of a claim campaign needed to check it suits a Status Claim landing page. */
export interface ClaimCampaignForCheck {
  validatorType: string;
  emailVerificationDomains: Array<string>;
  requireApproval?: boolean;
  actions: Array<Record<string, unknown>>;
}

export interface ClaimCampaignCheckResult {
  /** Problems that would stop the landing page from granting the tier */
  issues: Array<string>;
  /** How customers will claim, derived from the claim campaign (the landing page does the same) */
  summary: string;
}

/**
 * Checks a backend claim campaign against the Status Claim payload. The customer site derives the
 * claim flow from the campaign: Direct campaigns are granted automatically with no code, every other
 * validator asks for a promo code. An empty `issues` array means the campaign is compatible.
 */
export function checkStatusClaimCampaign(
  campaign: ClaimCampaignForCheck,
  tiersGranted: Array<string>,
): ClaimCampaignCheckResult {
  const issues: Array<string> = [];
  const isCodeless = campaign.validatorType === 'DIRECT';
  const domains = campaign.emailVerificationDomains.map((domain) => `@${domain}`).join(', ');

  if (isCodeless && campaign.emailVerificationDomains.length === 0) {
    issues.push(
      'Direct claim campaign has no email verification domains, so every logged-in Société member who opens the landing page would be granted the tier. Add the allowed email domains to the claim campaign.',
    );
  }

  if (isCodeless && campaign.requireApproval) {
    issues.push(
      'Claim campaign needs CS approval. Direct campaigns are granted automatically on the landing page, which can’t collect evidence or wait for approval.',
    );
  }

  const tierUpgradeTargets = campaign.actions
    .filter((action) => action.type === 'TIER_UPGRADE' && typeof action.targetTier === 'string')
    .map((action) => (action.targetTier as string).toLowerCase());
  const mismatchedTiers = tiersGranted.filter((tier) => !tierUpgradeTargets.includes(tier.toLowerCase()));
  if (tierUpgradeTargets.length === 0) {
    issues.push('Claim campaign has no Tier Upgrade action, so no tier will be granted.');
  } else if (mismatchedTiers.length > 0) {
    issues.push(
      `Tiers Granted (${mismatchedTiers.join(', ')}) don't match the claim campaign's Tier Upgrade action (${tierUpgradeTargets.join(', ')}).`,
    );
  }

  let summary: string;
  if (!isCodeless) {
    summary = domains
      ? `Customers enter a promo code (${campaign.validatorType}) and need a verified email on: ${domains}.`
      : `Customers enter a promo code (${campaign.validatorType}).`;
  } else if (domains) {
    summary = `No code: customers with a verified email on ${domains} are granted the tier automatically, anywhere on the site.`;
  } else {
    summary = 'No code and no email domains. This needs fixing before launch (see below).';
  }

  return { issues, summary };
}
